import { getBacktest } from "../../../services/backtestsApi";
import type { Backtest, BacktestScope, BacktestSummary } from "../../../types/backtests";
import type { ExitPlacementSliceRef, SimulateLegs, SimulatedLeg } from "../../../types/managementExplorer";
import type { TradePolicySimulatePolicy, TradePolicySimulateResponse } from "../../../types/tradePolicy";
import {
  comparisonRunTitlesFromPersistedTitle,
  type ComparisonRun,
  type ComparisonScopeSnapshot,
  type ComparisonTitleParseMode,
} from "./simulatorComparisonTypes";

const DEFAULT_POLICY_LIMIT = 5000;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function parsePayload(raw: unknown): Record<string, unknown> | null {
  if (isPlainObject(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return isPlainObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function extractPolicy(b: Record<string, unknown>, pObj: Record<string, unknown> | null): unknown {
  const top = b.policy_tree ?? b.policy;
  if (top != null) return top;

  if (!pObj) return null;

  const direct = pObj.policy;
  if (direct != null) return direct;
  const tree = pObj.policy_tree;
  if (tree != null) return tree;

  const job = pObj.job;
  if (isPlainObject(job)) {
    const req = job.request;
    if (isPlainObject(req) && req.policy != null) return req.policy;
    if (job.policy != null) return job.policy;
  }
  const req = pObj.request;
  if (isPlainObject(req) && req.policy != null) return req.policy;
  const simReq = pObj.simulate_request;
  if (isPlainObject(simReq) && simReq.policy != null) return simReq.policy;
  const input = pObj.input;
  if (isPlainObject(input) && input.policy != null) return input.policy;

  return null;
}

function extractResult(b: Record<string, unknown>, pObj: Record<string, unknown> | null): unknown {
  const top = b.result;
  if (top != null) return top;

  if (!pObj) return null;

  if (
    "baseline" in pObj ||
    "filtered" in pObj ||
    "delta" in pObj ||
    "coverage" in pObj ||
    "removed" in pObj ||
    "changed" in pObj
  ) {
    return pObj;
  }

  const direct = pObj.result;
  if (direct != null) return direct;
  const simRes = pObj.simulate_result;
  if (simRes != null) return simRes;
  const out = pObj.output;
  if (out != null) return out;

  const job = pObj.job;
  if (isPlainObject(job)) {
    const r = job.result ?? job.output;
    if (r != null) return r;
  }

  const metrics = pObj.metrics;
  if (isPlainObject(metrics) && ("baseline" in metrics || "filtered" in metrics)) {
    return metrics;
  }

  return null;
}

function readExitPlacementMeta(
  record: Record<string, unknown>,
  pObj: Record<string, unknown> | null
): {
  simulateLegs?: SimulateLegs;
  simulatedLegs?: SimulatedLeg[];
  exitPlacementSlices?: ExitPlacementSliceRef[];
} {
  const scope = scopeRecord(record.scope as BacktestScope | undefined);
  const scopeInputs =
    pObj?.scope_inputs && isPlainObject(pObj.scope_inputs)
      ? (pObj.scope_inputs as Record<string, unknown>)
      : null;
  const snap =
    record.input_snapshot && isPlainObject(record.input_snapshot)
      ? (record.input_snapshot as Record<string, unknown>)
      : pObj?.input_snapshot && isPlainObject(pObj.input_snapshot)
        ? (pObj.input_snapshot as Record<string, unknown>)
        : null;

  const simulateLegsRaw =
    scope.simulate_legs ?? scopeInputs?.simulate_legs ?? snap?.simulate_legs ?? pObj?.simulate_legs;
  const simulatedLegsRaw =
    scope.simulated_legs ?? scopeInputs?.simulated_legs ?? snap?.simulated_legs ?? pObj?.simulated_legs;
  const exitPlacementSlicesRaw =
    scope.exit_placement_slices ??
    scopeInputs?.exit_placement_slices ??
    scopeInputs?.slice_ids ??
    snap?.exit_placement_slices ??
    pObj?.exit_placement_slices;

  const out: {
    simulateLegs?: SimulateLegs;
    simulatedLegs?: SimulatedLeg[];
    exitPlacementSlices?: ExitPlacementSliceRef[];
  } = {};

  if (simulateLegsRaw === "sl_only" || simulateLegsRaw === "tp_only" || simulateLegsRaw === "both") {
    out.simulateLegs = simulateLegsRaw;
  }
  if (Array.isArray(simulatedLegsRaw)) {
    out.simulatedLegs = simulatedLegsRaw as SimulatedLeg[];
  }
  if (Array.isArray(exitPlacementSlicesRaw)) {
    if (exitPlacementSlicesRaw.every((entry) => typeof entry === "string")) {
      out.exitPlacementSlices = (exitPlacementSlicesRaw as string[]).map((slice_id) => ({ slice_id }));
    } else {
      out.exitPlacementSlices = exitPlacementSlicesRaw as ExitPlacementSliceRef[];
    }
  }

  return out;
}

function scopeRecord(scope: BacktestScope | undefined): Record<string, unknown> {
  if (!scope || !isPlainObject(scope)) return {};
  return scope as Record<string, unknown>;
}

function extractScopeSource(
  b: Record<string, unknown>,
  pObj: Record<string, unknown> | null,
  result: TradePolicySimulateResponse | null
): Record<string, unknown> {
  const topScope = scopeRecord(b.scope as BacktestScope | undefined);
  const meta = result?.meta && isPlainObject(result.meta) ? (result.meta as Record<string, unknown>) : null;

  const jobReq =
    pObj?.job && isPlainObject(pObj.job) && isPlainObject((pObj.job as Record<string, unknown>).request)
      ? ((pObj.job as Record<string, unknown>).request as Record<string, unknown>)
      : null;
  const req = pObj?.request && isPlainObject(pObj.request) ? (pObj.request as Record<string, unknown>) : null;
  const simReq =
    pObj?.simulate_request && isPlainObject(pObj.simulate_request)
      ? (pObj.simulate_request as Record<string, unknown>)
      : null;
  const input =
    pObj?.input && isPlainObject(pObj.input) ? (pObj.input as Record<string, unknown>) : null;

  return {
    ...topScope,
    ...(jobReq ?? {}),
    ...(req ?? {}),
    ...(simReq ?? {}),
    ...(input ?? {}),
    ...(meta ?? {}),
    ...(typeof b.local_start_date === "string" ? { local_start_date: b.local_start_date } : {}),
    ...(typeof b.local_end_date === "string" ? { local_end_date: b.local_end_date } : {}),
    ...(typeof b.timezone === "string" ? { timezone: b.timezone } : {}),
    ...(typeof b.timeframe === "string" ? { timeframe: b.timeframe } : {}),
    ...(typeof b.effective_start_date === "string" ? { start_date: b.effective_start_date } : {}),
    ...(typeof b.effective_end_date === "string" ? { end_date: b.effective_end_date } : {}),
  };
}

/** Build a comparison run card from a saved simulation (backtest) record. */
export function backtestToComparisonRun(b: Backtest | Record<string, unknown>): ComparisonRun | null {
  const record = b as Record<string, unknown>;
  const pObj = parsePayload(record.payload);
  const policy = extractPolicy(record, pObj);
  const resultRaw = extractResult(record, pObj);

  if (!policy || !resultRaw) return null;

  const result = resultRaw as TradePolicySimulateResponse;
  const src = extractScopeSource(record, pObj, result);

  const localStartDate =
    (typeof src.local_start_date === "string" && src.local_start_date) ||
    (typeof src.start_date === "string" && src.start_date.length >= 10 ? src.start_date.slice(0, 10) : undefined);
  const localEndDate =
    (typeof src.local_end_date === "string" && src.local_end_date) ||
    (typeof src.end_date === "string" && src.end_date.length >= 10 ? src.end_date.slice(0, 10) : undefined);
  const tz = typeof src.timezone === "string" ? src.timezone : undefined;
  const tf = typeof src.timeframe === "string" ? src.timeframe : undefined;
  const candleRaw = typeof src.candle_timeframe === "string" ? src.candle_timeframe.trim() : "";
  const candleTimeframe =
    candleRaw === "1m" || candleRaw === "5m" || candleRaw === "15m" || candleRaw === "30m" || candleRaw === "1h" || candleRaw === "4h"
      ? candleRaw
      : undefined;

  const keptIds = result.kept_trade_ids ?? [];
  const removedIds = result.removed_trade_ids ?? [];

  const scope: ComparisonScopeSnapshot = {
    useCustomRange: Boolean(localStartDate && localEndDate),
    ...(localStartDate && localEndDate ? { localStartDate, localEndDate, timezone: tz } : tf ? { timeframe: tf } : {}),
    symbol: typeof src.symbol === "string" ? src.symbol : "",
    limit: typeof src.limit === "number" ? src.limit : DEFAULT_POLICY_LIMIT,
    includeTradeIds: Boolean(src.include_trade_ids) || keptIds.length > 0 || removedIds.length > 0,
    ...(candleTimeframe && candleTimeframe !== "1m" ? { candleTimeframe } : {}),
  };

  const createdAt =
    typeof record.created_at === "string" ? Date.parse(record.created_at) : Date.now();
  const id = String(record.id ?? "");
  const linkedStrategyId =
    (typeof record.strategy_id === "string" && record.strategy_id.trim()) ||
    (pObj && typeof pObj.strategy_id === "string" && pObj.strategy_id.trim()) ||
    (typeof src.strategy_id === "string" && src.strategy_id.trim()) ||
    undefined;

  const rawTitle = String(record.title ?? "").trim() || `Saved simulation ${id.slice(0, 8)}…`;
  const titles = comparisonRunTitlesFromPersistedTitle(rawTitle);
  const exitMeta = readExitPlacementMeta(record, pObj);

  return {
    id: `saved_${id}`,
    label: titles.label,
    savedTitle: titles.savedTitle,
    ...(titles.titleParseMode ? { titleParseMode: titles.titleParseMode } : {}),
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    policy: policy as TradePolicySimulatePolicy,
    scope,
    result,
    error: null,
    status: "succeeded",
    backtestId: id,
    isSavedBacktest: true,
    ...(linkedStrategyId ? { linkedStrategyId } : {}),
    ...(exitMeta.simulateLegs ? { simulateLegs: exitMeta.simulateLegs } : {}),
    ...(exitMeta.simulatedLegs ? { simulatedLegs: exitMeta.simulatedLegs } : {}),
    ...(exitMeta.exitPlacementSlices ? { exitPlacementSlices: exitMeta.exitPlacementSlices } : {}),
  };
}

/** Hydrate a list/detail row; fetches full backtest when directory rows omit result blobs. */
export async function hydrateBacktestToComparisonRun(
  b: Backtest | Record<string, unknown>
): Promise<ComparisonRun | null> {
  const direct = backtestToComparisonRun(b);
  if (direct) return direct;

  const id = String((b as Record<string, unknown>).id ?? "").trim();
  if (!id) return null;

  try {
    const full = await getBacktest(id, { include_result: true });
    return backtestToComparisonRun(full);
  } catch {
    return null;
  }
}

/** Apply saved-title fields from simulate save `backtest_summary` without a follow-up GET. */
export function comparisonRunTitlesFromBacktestSummary(
  summary: BacktestSummary
): { savedTitle: string; label: string; titleParseMode?: ComparisonTitleParseMode } | null {
  const rawTitle = summary.title?.trim();
  if (!rawTitle) return null;
  const titles = comparisonRunTitlesFromPersistedTitle(rawTitle);
  if (!titles.savedTitle) return null;
  return titles;
}
